/**
 * Виджеты категории "Навигация": поиск, часто посещаемые сайты, закладки,
 * ссылки, сервисы Google, переключатель рабочих столов.
 *
 * Факты — по New Tab Studio 4.0.0:
 * data/screenshots/report-4-navigation-design.md и report-4-new-widgets.md.
 *
 * @see docs/seo/SEO-STRATEGY.md
 * @see docs/SEO_WRITER.md
 */

export default [
	{
		slug: 'search-bar',
		name: 'Search Bar',
		category: 'Navigation',
		plan: 'free',
		model: 'SearchBarModel',
		title: 'Search Bar for Chrome with 13 Engines',
		metaDescription:
			'Chrome new tab search bar missing? Put it back with 13 engines from Google to ChatGPT, six designs and recent searches that never leave your browser.',
		h1: 'Search bar for Chrome, back on your new tab',
		lead:
			'Chrome new tab search bar missing? Add this widget and you’ve got a search bar for Chrome again, with your engine, your design and your spot on the page. Why it vanished in the first place: an extension that replaces the new tab page takes Chrome’s own search box with it, and New Tab Studio is one of those extensions.',
		screenshot: {
			file: 'search-bar-hero.webp',
			alt:
				'Glass search bar with ’how to make’ typed: three recent searches with clock icons above Google suggestions',
			caption:
				'Your own recent searches come first, Google’s suggestions follow',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Thirteen engines, switched from inside the bar',
				p: [
					'In the newer designs the engine icon at the left edge of the bar opens a menu in three groups, starting with the regular engines (Browser default, Google, Bing, Yandex, DuckDuckGo, Ecosia, Brave Search and Yahoo), then Ask AI with ChatGPT and Perplexity, then Search a site with YouTube, Wolfram Alpha and Wikipedia in your interface language. Thirteen in all.',
					'Browser default hands the query to whatever Chrome already uses. Worried a new tab extension will hijack your search? Pick that one — question closed.',
					'The empty box follows the engine, so it reads "Search Google or type a URL", "Ask ChatGPT" or "Search YouTube". Prefer your own words, a greeting or a nudge to yourself? Type them into Input placeholder. Clear the field and the engine’s text comes back.',
				],
				image: {
					file: 'search-bar-engines.webp',
					alt:
						'Spotlight search bar with the engine menu open: search engines, Ask AI and Search a site, Google ticked',
					caption:
						'Thirteen ways to search, two of them AI assistants',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Six designs, and what’s left to tweak',
				p: [
					'Classic, Spotlight, Glass, Minimal, Dark and Paper. A new bar arrives as Spotlight in Inter, and Spotlight, Glass and Dark add a search button on the right. Bars saved in older themes stay Classic, so an update does not restyle anything you built.',
					'Classic is the one for full manual control over font, size, text colour, fill and corners. The newer designs fix fill, border and size themselves and leave you the font; Minimal also lets you recolour the text and the line. If you want a different look, switch the design rather than wrestling the toolbox, because each of the newer designs was drawn as a whole and looks best left that way.',
				],
			},
			{
				h2: 'Suggestions, recent searches, privacy',
				p: [
					'Suggestions start off. Turn them on and Chrome asks for access to suggestqueries.google.com. From then on, whatever you type goes to Google once you pause for 150 ms, and up to eight suggestions come back in your interface language. With YouTube as the engine, YouTube suggests instead.',
					'Recent searches are on from the start. They stay in this browser, and themes, exports and anything you publish never carry them along, however many people end up installing your page. The bar keeps 20 at most, skips anything over 200 characters and shows six when the box is empty. Shift+Delete drops one, Clear history in settings drops the lot.',
					'The microphone button shows up where the browser can do speech recognition. Recognition goes through the browser’s own speech API.',
				],
			},
			{
				h2: 'Shortcuts that save a few seconds a day',
				p: [
					'Press / on an empty spot of the page. The cursor jumps into the bar, same as on GitHub or YouTube. Type an address such as github.com, localhost or an IP, and "Open site" goes there directly instead of searching for it.',
					'Alt+Enter, Ctrl+Enter (Cmd on a Mac) or a middle click opens results in a new tab. There’s a switch to make that the default. And typing Japanese or Chinese through an IME no longer fires a search halfway through a word.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Spotlight, Glass, Minimal, Dark or Paper, picked from live previews.',
			},
			{
				name: 'Search engine',
				detail:
					'A grid of 13 cards. The newer designs also switch engines from the icon inside the bar.',
			},
			{
				name: 'Search suggestions',
				detail:
					'Off by default. Turning it on asks for access to Google’s suggestion service.',
			},
			{
				name: 'Show recent searches',
				detail:
					'On by default, stored in this browser only, with a counter and Clear history.',
			},
			{
				name: 'Open results in a new tab',
				detail:
					'Off by default. Modifier keys and a middle click open a new tab either way.',
			},
			{
				name: 'Voice search button',
				detail: 'Where the browser supports speech recognition.',
			},
			{
				name: 'Input placeholder',
				detail:
					'Your text for the empty box. Leave it blank and the wording follows the engine.',
			},
		],
		useCases: [
			'Getting a search box back after an extension took over the new tab page.',
			'Ecosia or DuckDuckGo on the page while the address bar stays on Google.',
			'A second bar pointed at Perplexity for questions rather than links.',
			'Straight into YouTube or Wikipedia search without loading the site first.',
			'A custom placeholder that asks you what you came here to do.',
		],
		faq: [
			{
				q: 'How do I get the search bar back on my Chrome new tab?',
				a:
					'The search bar usually disappears from Chrome’s new tab because an extension replaced the page, and switching that extension off at chrome://extensions brings Google’s box back. Prefer to keep the extension? In New Tab Studio, press the button in the bottom-left corner, open Navigation and add Search bar. It works with any of 13 engines, Google included.',
			},
			{
				q: 'How do I change the search engine on my new tab page?',
				a:
					'Chrome ties the search on its own new tab to the default engine under Settings → Search engine, and changing it changes the address bar too. The search bar widget keeps an engine of its own, so the page can search Ecosia or DuckDuckGo while the address bar stays on Google. In the newer designs, click the engine icon at the left of the bar to switch.',
			},
			{
				q: 'Does New Tab Studio change my default search engine?',
				a:
					'No, New Tab Studio doesn’t change your default search engine, since it never registers a search provider with Chrome. The address bar keeps whatever you chose. The widget’s Browser default option hands your query to that same engine through Chrome’s search permission.',
			},
			{
				q: 'Is the search bar widget free?',
				a:
					'Yes, the search bar is free with nothing held back, from all 13 engines and six designs to suggestions and recent searches. The only cap is the general one — the free plan lets you add three widgets by hand, and a bar that arrived inside an installed theme isn’t counted.',
			},
			{
				q: 'What does the search bar send over the network?',
				a:
					'The search bar sends your query to the engine you picked when you press Enter, and nothing before that unless suggestions are on. With them switched on, what you type reaches Google’s suggestion service after a 150 ms pause. Recent searches stay in this browser, 20 at most, and never travel with a theme or an export.',
			},
		],
		related: ['top-sites', 'google-apps', 'bookmarks', 'link'],
		keywords: [
			'search bar for chrome',
			'chrome new tab search bar missing',
			'new tab search bar',
			'custom search bar new tab',
		],
	},

	{
		slug: 'top-sites',
		name: 'Top Sites',
		category: 'Navigation',
		plan: 'free',
		model: 'TopSitesModel',
		title: 'Speed Dial Chrome Extension for New Tab',
		metaDescription:
			'A speed dial for Chrome’s new tab: most visited sites plus your own, dragged into order, pinned and renamed. Six designs, up to 40 tiles, undo on delete.',
		h1: 'Top sites: a speed dial for Chrome’s new tab',
		lead:
			'Want a speed dial Chrome extension that also knows your most visited sites? Top sites reads Chrome’s ranking, then lets you pin, reorder, rename and add your own. Came here to edit Chrome’s own new tab shortcuts instead? That happens on Chrome’s page, and you do not need us for it.',
		screenshot: {
			file: 'top-sites-hero.webp',
			alt:
				'Eight frequently visited sites as colored cards in the middle and as an icon dock at the bottom, over a night city',
			caption:
				'The same eight sites twice: as cards in each site’s color and as a dock',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Chrome’s shortcuts or this widget?',
				p: [
					'Chrome’s built-in new tab page has shortcut tiles of its own, and you edit them right there. Install a new tab extension, this one included, and that page is replaced, tiles and all.',
					'The widget picks up where those tiles stop. Show up to 40 sites or every one Chrome ranks, set the columns, pick one of six designs. "Sites to show" switches between "Most visited + mine" and "Only mine", and the second turns it into a plain speed dial with no browsing history in it.',
				],
			},
			{
				h2: 'An order that stays put',
				p: [
					'In edit mode, drag a tile to where it belongs, on the widget or in the settings list. Everything up to the drop point gets pinned, so next week’s ranking can’t shuffle it back. "Pin to the start" does the same for a single site.',
					'Delete a site and it’s hidden for good. Clicked the wrong ✕? An Undo waits at the bottom of the screen for six seconds, and older removals come back from "Hidden sites" with Show again.',
					'Adding got quicker too. Paste an address into settings, or drag a link from the address bar and drop it on the widget. That’s it.',
				],
				image: {
					file: 'top-sites-reorder.webp',
					alt:
						'Top sites tiles in edit mode: YouTube and Wikipedia pinned, the Duolingo tile being dragged to a new place',
					caption:
						'Drag a site where you want it, and it stays pinned there',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Six designs',
				p: [
					'Classic is the old row of icons, with pixel sizes from 16 to 128. Tiles (what a new widget gets, eight sites in Inter), Dock, List, Cards and Icons only size their frame to the number of sites and columns by themselves. Cards paint every tile in the main colour of the site’s icon. Those colours are kept in this browser and don’t go into your theme.',
					'Labels depend on the design. Classic, Tiles and Dock can hide them, List and Cards always show them, Icons only shows a name on hover.',
					'Any site can be renamed and given its own icon — a letter, the site’s icon, an emoji or a picture of yours.',
				],
			},
			{
				h2: 'Permission and icons',
				p: [
					'Reading your most visited sites takes Chrome’s topSites permission, asked in a single prompt when you add the widget. Say no and nothing breaks. The widget stays, shows the sites you added and offers an Allow access button for later.',
					'Small icons come from the browser’s own favicon store first, while large ones, and whatever the browser lacks, are requested from Google’s favicon service, with misses remembered for three days so the same dead icon isn’t asked for on every tab. Localhost, IP addresses and .local names are never sent to Google.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail: 'Classic, Tiles, Dock, List, Cards or Icons only.',
			},
			{
				name: 'Sites',
				detail:
					'"Paste or type an address" to add one. Edit, pin or remove from the list; Show again brings hidden sites back.',
			},
			{ name: 'Number of sites', detail: 'From 1 to 40, or All.' },
			{
				name: 'Columns',
				detail: '1 to 10 or Auto. Not offered for Classic and Dock.',
			},
			{
				name: 'Icon size / Icon shape',
				detail:
					'Pixel sizes from 16 to 128 in Classic; Rounded or Circle in the other designs.',
			},
			{
				name: 'Show titles',
				detail: 'A switch in Classic, Tiles and Dock.',
			},
			{
				name: 'Sites to show',
				detail: 'Most visited + mine, or Only mine.',
			},
			{
				name: 'Open in a new tab',
				detail: 'Leave it off to open sites in the tab you’re on.',
			},
		],
		useCases: [
			'A speed dial of hand-picked sites, with browsing history kept out.',
			'Most visited sites minus the ones you’d rather not be reminded of.',
			'A dock of eight icons along the bottom of a photo theme.',
			'A List design as a narrow column of work tools.',
		],
		faq: [
			{
				q: 'How do I edit the shortcuts on Chrome’s new tab page?',
				a:
					'You edit the shortcuts on Chrome’s new tab page from the page itself. Open Customize Chrome in the bottom-right corner, choose My shortcuts under Shortcuts, then hover over a tile and use its three-dot menu to edit or remove it. A new tab extension replaces those tiles, and the Top sites widget takes over with drag-to-reorder, renaming and custom icons.',
			},
			{
				q: 'How do I get my most visited sites back on the new tab?',
				a:
					'To get most visited sites back on Chrome’s own new tab, open Customize Chrome, go to Shortcuts, switch them on and choose Most visited sites. If an extension replaced that page, the tiles left with it. New Tab Studio’s Top sites widget reads the same Chrome ranking, shows up to 40 of those sites and brings removed ones back with Show again.',
			},
			{
				q: 'Is there a free speed dial extension for Chrome?',
				a:
					'Yes, the Top sites widget in New Tab Studio works as a free speed dial on Chrome’s new tab, with six designs and up to 40 tiles. Set Sites to show to Only mine and it holds just the addresses you picked, with no browsing history and no permission involved. It fills one hand-added slot of the three on the free plan.',
			},
			{
				q: 'If I remove a site by mistake, can I get it back?',
				a:
					'Yes, an Undo button waits at the bottom of the screen for six seconds after you remove a site from Top sites. Missed it? Open settings and press Show again under Hidden sites. Otherwise a removed site stays hidden for good, however high Chrome keeps ranking it.',
			},
			{
				q:
					'Does the Top sites widget send my browsing history anywhere?',
				a:
					'No, the Top sites widget gets its list from Chrome and draws it on the page without sending it anywhere. The one outside request is for icons the browser hasn’t stored, looked up at Google’s favicon service by address. Localhost, IP addresses and .local names are never sent to Google at all.',
			},
		],
		related: ['bookmarks', 'link', 'search-bar', 'google-apps'],
		keywords: [
			'speed dial chrome extension',
			'chrome new tab shortcuts',
			'most visited sites chrome new tab',
			'top sites new tab',
		],
	},

	{
		slug: 'bookmarks',
		name: 'Bookmarks',
		category: 'Navigation',
		plan: 'free',
		model: 'BookmarksModel',
		title: 'Bookmarks Chrome Extension for New Tab',
		metaDescription:
			'Show bookmarks on the new tab page as a bar, app tiles, a searchable list, a glass panel or a dock. Your real Chrome bookmarks, so you can hide Chrome’s bar.',
		h1: 'Show bookmarks on the new tab, five ways',
		lead:
			'This is the bookmarks Chrome extension part of New Tab Studio: your actual bookmarks on the new tab page, in the design you choose. Hide Chrome’s own bar with Ctrl+Shift+B (⌘⇧B on a Mac) and every other tab gets that strip back, while your bookmarks wait on the page where you decide where to go.',
		screenshot: {
			file: 'bookmarks-hero.webp',
			alt:
				'Glass bookmarks panel with Work, Reading, Learn and Travel folders as columns of site icons, under a clock',
			caption:
				'Every bookmarks folder at a glance, no digging through the bar',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Pick a design when you add it',
				p: [
					'Adding the widget opens "How should your bookmarks look?" with five answers. Bar at the top is the familiar strip across the window. Tiles shows sites and folders as app icons, List is compact and searchable, Glass panel lays out all your folders at once on frosted glass, and Dock gives your favourite sites big icons.',
					'Close the window without choosing and you get the bar. Only the bar stretches edge to edge; the other four are ordinary widgets you drag and resize.',
				],
				image: {
					file: 'bookmarks-chooser.webp',
					alt:
						'Dialog asking how bookmarks should look: Bar at the top as default, then Tiles, List, Glass panel and Dock',
					caption:
						'Right after adding: pick a design or keep the classic bar',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Your real bookmarks, kept live',
				p: [
					'Nothing is imported or copied — the widget reads Chrome’s bookmarks and redraws itself when they change, whether you star a page, move a folder or import from another browser.',
					'It shows bookmarks and does not manage them. Adding, renaming and deleting happen in Chrome as usual, and the change turns up here a moment later.',
					'Folder to show narrows the view. Start it at "Work" or "Recipes" and that folder is all the page shows, while Chrome keeps the full tree. Folders open inside the widget.',
				],
			},
			{
				h2: 'The bar, sized in real pixels',
				p: [
					'Bar at the top keeps its height in screen pixels, 30 by default and anywhere from 24 to 72 when you resize it. A small window no longer squashes it flat. Out of the box it’s white with dark Roboto text, close enough to Chrome’s to need no relearning.',
					'Drop it onto a page that already has widgets along the top and they slide down to sit just below it. Put a search bar right underneath and the top of the page turns into a working header. Turn Show titles off and the bar becomes a row of icons.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail: 'Bar at the top, Tiles, List, Glass panel or Dock.',
			},
			{
				name: 'Folder to show',
				detail:
					'All bookmarks (the bookmarks bar plus your other folders) or any single folder.',
			},
			{
				name: 'Open in a new tab',
				detail:
					'Your call. A middle click opens a new tab whatever this says.',
			},
			{
				name: 'Show titles',
				detail:
					'In Bar, Tiles and Dock. For the bar, off means icons only.',
			},
		],
		useCases: [
			'Hiding Chrome’s bookmarks bar without losing the bookmarks.',
			'One folder of work links as tiles on a work workspace.',
			'A searchable list for anyone who bookmarks everything.',
			'A dock of big icons for the five sites you open daily.',
		],
		faq: [
			{
				q: 'How do I show bookmarks on the Chrome new tab page?',
				a:
					'Chrome’s built-in way to show bookmarks is Ctrl+Shift+B (⌘⇧B on a Mac), which turns the bookmarks bar on for every tab. To keep them on the new tab page alone, add the Bookmarks widget in New Tab Studio. It reads your real Chrome bookmarks and comes in five designs, from a bar at the top to tiles, a searchable list, a glass panel and a dock.',
			},
			{
				q: 'Can I show only one bookmarks folder on my new tab?',
				a:
					'Yes, the Bookmarks widget’s Folder to show setting starts the view at any single folder, say Work or Recipes, and the page shows only that one. Chrome keeps the full tree. Subfolders open right inside the widget, and the List design adds a search box for anyone with hundreds of bookmarks.',
			},
			{
				q: 'Do I have to import my bookmarks into a new tab extension?',
				a:
					'No import is needed with New Tab Studio, because the Bookmarks widget reads what’s already in Chrome and redraws itself whenever that changes, synced bookmarks from other devices included. Editing stays in Chrome. The widget only shows bookmarks and can’t add, rename or delete them.',
			},
			{
				q: 'Is it free to show bookmarks on the new tab?',
				a:
					'Yes, all five bookmark designs, folder views and search are free. The widget asks for Chrome’s bookmarks permission once, when you add it, and if you decline, a Show my bookmarks button waits to ask again. Placed by hand, it takes a widget slot, and the free plan has three of those.',
			},
		],
		related: ['top-sites', 'link', 'search-bar', 'google-apps'],
		keywords: [
			'bookmarks chrome extension',
			'show bookmarks on new tab',
			'chrome new tab bookmarks bar',
			'new tab bookmarks',
		],
	},

	{
		slug: 'link',
		name: 'Link',
		category: 'Navigation',
		plan: 'free',
		model: 'LinkModel',
		title: 'Custom Link Shortcuts for Chrome New Tab',
		metaDescription:
			'One button, one label, one spot you picked. Turn any address into a new tab shortcut with a site icon, a letter, an emoji or your own image, placed by hand.',
		h1: 'Link widget: new tab shortcuts you place by hand',
		lead:
			'New tab shortcuts don’t have to live in a grid. A link widget is one round button with a label under it, sitting exactly where you dropped it. Top sites ranks for you, bookmarks follow a folder tree, and this follows nothing but your hand.',
		screenshot: {
			file: 'link-launcher.webp',
			alt:
				'Six round links: Gmail and GitHub icons, a headphones emoji, a beach photo, letters D and R on dark and light circles',
			caption:
				'Site icon, emoji, your own picture, or a letter that picks a readable color',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Where it sits is the point',
				p: [
					'Work on the left, personal on the right. The job you should be doing big and central; the site you drift to instead small and in a corner, where it can think about what it did.',
					'A grid launcher can’t say any of that, because the grid picks the arrangement. Here the arrangement is yours.',
				],
			},
			{
				h2: 'Four kinds of icon',
				p: [
					'Site icon, Letter, Emoji or Image. A new link starts with the site’s icon on a white circle, and the settings window opens straight away, so you type the address and a label and you’re done.',
					'Letters got smarter in 4.0 — give the circle any colour and the letter turns white on dark or near-black on light, no picking required. Site icons, emoji and pictures get a soft shadow, like app icons on a phone.',
					'Your own picture is saved into the theme as a small 96×96 PNG, so it travels with a theme you share.',
				],
			},
			{
				h2: 'Colour, border and size',
				p: [
					'The label always sits under the circle, straight on the wallpaper. Text colour in the toolbox paints the label and the fill paints the circle; take the fill away and you lose the circle, not the label. The Border panel draws its outline on the circle itself.',
					'There is no font size control here. Resize the widget and circle and label grow together, in fixed proportions.',
				],
			},
			{
				h2: 'A launcher on the free plan',
				p: [
					'Three hand-added widgets are free, so a free launcher is three links, or two links and a clock. Widgets that came with an installed theme do not count toward that. Want a wall of twelve? That’s PRO.',
					'Any address works, deep links into one board or document included. Skip the http:// and it’s added for you. Links open in the tab you’re on.',
				],
			},
		],
		settings: [
			{
				name: 'Label',
				detail:
					'Free text under the circle. A name, a letter or a verb like "Write".',
			},
			{
				name: 'Url address',
				detail:
					'Any address. Saved a moment after you stop typing, so the icon doesn’t flicker.',
			},
			{ name: 'Icon', detail: 'Letter, Site icon, Emoji or Image.' },
			{
				name: 'Colours and border',
				detail:
					'In the toolbox, in edit mode. Fill colours the circle, text colour the label.',
			},
			{
				name: 'Size',
				detail:
					'Resize the widget itself; the proportions stay locked.',
			},
		],
		useCases: [
			'A launcher arranged by what matters, not by visit count.',
			'Deep links into a single board, document or dashboard.',
			'A local development server on a work workspace.',
			'One oversized button for today’s main job.',
		],
		faq: [
			{
				q: 'How do I add a website shortcut to my Chrome new tab?',
				a:
					'On Chrome’s own new tab, pick My shortcuts under Customize Chrome → Shortcuts and use the Add shortcut tile, and the site joins the grid under the search box. To put a shortcut anywhere on the page instead, add a link widget in New Tab Studio. It’s one round button with a label, sitting exactly where you dropped it.',
			},
			{
				q: 'Can I use an emoji or my own picture as a shortcut icon?',
				a:
					'Yes, a link widget has four icon types (the site’s icon, a letter, an emoji or an image of yours). Your own picture is saved into the theme as a small 96×96 PNG, so it travels with a theme you share. Letters turn white or near-black by themselves to stay readable on any circle colour.',
			},
			{
				q: 'Do link shortcuts open in a new tab?',
				a:
					'No, a link widget opens its address in the tab you’re already on, since you opened that tab to go somewhere. Any address works, deep links into a single board or document included. Skip the http:// and it gets added for you.',
			},
			{
				q: 'How many link shortcuts can I add for free?',
				a:
					'You can add three link shortcuts for free, because each link is its own widget and the free plan covers three placed by hand. Links that came with an installed theme don’t count, so a theme full of them stays free. Want a bigger launcher of your own? A Top sites widget fits up to 40 sites into one slot.',
			},
			{
				q: 'How is a link widget different from a bookmark?',
				a:
					'A bookmark lives in Chrome and syncs wherever Chrome does, while a link widget lives at one spot on your new tab page and looks the way you styled it. Link widgets also go along when you share the theme. For your actual bookmarks on the page, there’s a separate Bookmarks widget.',
			},
		],
		related: ['top-sites', 'bookmarks', 'google-apps', 'figure'],
		keywords: [
			'new tab shortcuts',
			'custom links new tab chrome',
			'quick links new tab',
			'launcher new tab extension',
		],
	},

	{
		slug: 'google-apps',
		name: 'Google Apps Links',
		category: 'Navigation',
		plan: 'free',
		model: 'GoogleLinksModel',
		title: 'Google Apps Widget for Chrome New Tab',
		metaDescription:
			'Gmail, Drive, Docs, Maps, Gemini and 16 more Google apps on your new tab, in the order you set. Six designs, from the classic corner links to a dock.',
		h1: 'Google apps links on your new tab, in your order',
		lead:
			'Google apps links on the new tab page, arranged your way: Gmail first, Gemini second, Earth nowhere at all. A new tab extension takes Google’s own shortcuts away with Chrome’s page, and this widget brings them back with a say in what’s there.',
		screenshot: {
			file: 'google-apps-hero.webp',
			alt:
				'Google apps grid of nine services in the top right corner, with settings open on a list of apps with toggles',
			caption:
				'Switch Google apps on or off, then drag them into your order',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Choose the apps, then the order',
				p: [
					'The Google apps section of settings reads "Turn apps on and put them in order". There are 21 to pick from. Search, Gmail, Images, Drive, Docs, Sheets, Slides, Calendar, Maps, YouTube, Photos, Translate, News, Gemini, Meet, Chat, Keep, Play, Lens, Earth and Account.',
					'A new widget starts with nine in a 3×3 grid (Gmail, Images, Drive, YouTube, Maps, Docs, Calendar, Photos and Gemini). Switch off what you never open (Earth, for most of us) and drag the rest into place.',
					'Docs, Sheets and Slides share one domain yet each gets its own icon. The icon is looked up by page address, not by domain.',
				],
			},
			{
				h2: 'Six designs',
				p: [
					'Classic is the old "Mail · Images · apps" cluster with its three switches and a fixed menu of 25 services. Top bar lines links up in a strip with an apps menu. App grid is what a new widget gets. Dock sets the icons in a row, Search bar puts a Google search box in the widget with apps beside it, and List stacks them in up to three columns.',
					'There’s also a style to pick (Glass, Light or Dark). Add a new widget to a page with a light solid background and it picks Light by itself.',
					'A Classic quirk worth knowing: switch off all three parts and nothing is left on screen.',
				],
				image: {
					file: 'google-apps-styles.webp',
					alt:
						'Google apps as search bar, app grid, list and dock, light style on the left and dark style on the right',
					caption: 'Four layouts, each in a light and a dark style',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Why one widget beats five links',
				p: [
					'You could rebuild this from link widgets. Why bother? The free plan gives you three hand-added widgets, and this one fits up to 21 apps into a single slot.',
					'It places itself top right and drops below the bookmarks bar if one is there. Move it anywhere after that.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Top bar, App grid, Dock, Search bar or List, plus a Glass, Light or Dark style.',
			},
			{
				name: 'Google apps',
				detail: '21 services to switch on and drag into order.',
			},
			{
				name: 'Columns',
				detail: '2 to 6 in App grid, 1 to 3 in List.',
			},
			{ name: 'Show app names', detail: 'App grid and Dock.' },
			{
				name: 'Links in the bar / Icons in the bar',
				detail:
					'Zero to four up front in Top bar and Search bar; the rest wait in the apps menu.',
			},
			{
				name: 'Open links in a new tab',
				detail:
					'Gmail and the rest open in a fresh tab instead of this one.',
			},
			{
				name: 'Show mail, Show images, apps menu',
				detail: 'Classic only, three separate switches.',
			},
		],
		useCases: [
			'Gmail, Calendar and Drive back in the corner after a new tab extension removed them.',
			'A dock of the four Google tools your job runs on.',
			'A Google search box with the apps menu beside it.',
			'A List design with Docs, Sheets and Slides for a study workspace.',
		],
		faq: [
			{
				q:
					'How do I get Gmail and the Google apps grid back on my new tab?',
				a:
					'Gmail, Images and the Google apps grid sit in the top-right corner of Chrome’s own new tab, so they return as soon as you switch off the extension that replaced the page. Keeping New Tab Studio? Its Google apps widget puts them back in that corner, starting with nine apps in a 3×3 grid, Gmail and Drive among them.',
			},
			{
				q: 'Can I choose which Google apps show up and in what order?',
				a:
					'Yes, the Google apps widget lists 21 services, from Gmail and Drive to Gemini, Keep and Earth, and you switch each on or off and drag them into order. Classic is the exception, with a fixed menu of 25 services. Anything Google that isn’t listed can go on a link widget instead.',
			},
			{
				q: 'Does the Google apps widget work with a Workspace account?',
				a:
					'The Google apps widget works with a Workspace account the way Google’s own links do, opening each service in the account the browser is signed into. A custom Workspace domain can’t be swapped in, though. And since these are links rather than a mail client, there’s no unread count.',
			},
			{
				q: 'Does it cost anything to add Google apps to the new tab?',
				a:
					'No, the Google apps widget is free in every design, all 21 apps included. It’s also the thrifty choice on the free plan — five separate links would blow past the three widgets you can add yourself, while this packs up to 21 apps into one.',
			},
		],
		related: ['link', 'search-bar', 'bookmarks', 'google-calendar'],
		keywords: [
			'google apps links new tab',
			'gmail link new tab',
			'google shortcuts new tab page',
			'google apps chrome new tab',
		],
	},

	{
		slug: 'workspaces',
		name: 'Workspace Switcher',
		category: 'Navigation',
		plan: 'mixed',
		model: 'WorkspaceModel',
		title: 'New Tab Dashboard: Workspace Switcher',
		metaDescription:
			'Split your Chrome new tab into separate dashboards for work, study and home, one click apart. Name them, copy widgets across, give each its own wallpaper.',
		h1: 'Workspaces: a new tab dashboard for every part of the day',
		lead:
			'One new tab dashboard can’t serve work, study and home at once, so it fills up with widgets that matter a third of the time. Workspaces split it into separate pages, each with its own widgets, and a small switcher flips between them. The free plan comes with three.',
		screenshot: {
			file: 'workspaces-hero.webp',
			alt:
				'Workspace tabs Work, Study and Home at the top with Work highlighted; a clock, the date and a Work task list',
			caption:
				'Name your workspaces and switch between them from the tabs',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'What a workspace is',
				p: [
					'Every widget belongs to one workspace, so switching swaps the whole page rather than filtering it. The switcher is the exception. You place it once, it shows on every workspace, and it does not count toward your free widgets.',
					'The wallpaper is shared until you say otherwise. Open the background window on a workspace called Work, turn on Only for workspace "Work", and that page gets a picture of its own. A background set this way stays fixed; automatic changes only run on the shared one. Use the common background undoes it.',
				],
			},
			{
				h2: 'How people split them',
				p: [
					'Work and personal is the usual split. A Chrome dashboard for the job with tasks, deadlines and a calendar on one; a photo, a habit tracker and a few links on the other. Students add a third for a course, exam countdown and all.',
					'The move that changes the most is parking distractions on a different workspace from your deadlines. The links still exist. They just stop being the first thing you see.',
				],
			},
			{
				h2: 'Names, tabs, layout',
				p: [
					'Give each workspace a name of up to 16 characters, then switch on "Show workspace names in the widget" to get tabs instead of dots. Names alone won’t do it, and the switch starts off.',
					'Layout goes Horizontal or Vertical. A column of named tabs down the left edge suits a wide monitor.',
				],
				image: {
					file: 'workspaces-settings.webp',
					alt:
						'Workspaces settings: workspace count, name fields Work, Study and Home, show names switch, copy widgets',
					caption:
						'Count, names and look of the switcher, plus copying widgets across',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Three free, and the catch with copying',
				p: [
					'Three is what the free plan runs. Any other count is PRO, including two, and five is the ceiling.',
					'Copy widgets to workspace duplicates widgets only, not the wallpaper or the name. Copies count as widgets you added yourself, so on the free plan they use up the limit of three, and a copy that would go over it doesn’t happen at all; the PRO window shows instead. That catches people who built a workspace from a theme. Theme widgets are free right up until you copy them — then they count.',
				],
			},
		],
		settings: [
			{
				name: 'Workspace count',
				detail: '2, 3, 4 or 5. Three is free, the rest are marked PRO.',
			},
			{
				name: 'Names',
				detail: 'One field per workspace, up to 16 characters.',
			},
			{
				name: 'Show workspace names in the widget',
				detail: 'Tabs with names instead of dots. Off by default.',
			},
			{ name: 'Layout', detail: 'Horizontal or Vertical.' },
			{
				name: 'Copy widgets to workspace',
				detail:
					'Pick the target and press Copy. Widgets only, no wallpaper.',
			},
			{
				name: 'Only for workspace N',
				detail:
					'Lives in the background window once you have two or more workspaces.',
			},
		],
		useCases: [
			'A work dashboard and a personal page that never mix.',
			'A study workspace per course, each with its exam countdown.',
			'A page for the sites you’d rather not see during work hours.',
			'A clean layout kept aside for screen sharing.',
		],
		faq: [
			{
				q: 'How can I create my own dashboard on the Chrome new tab?',
				a:
					'Chrome’s own new tab lets you change the background, shortcuts and colours, but it can’t hold widgets, so a real dashboard takes an extension. In New Tab Studio you drop widgets anywhere on the page (tasks, a calendar, a clock), and the Workspaces switcher keeps separate dashboards for work, study and home. Three come free.',
			},
			{
				q: 'How do I keep separate new tab pages for work and home?',
				a:
					'Workspaces give one Chrome new tab several pages, each with its own widgets, and a small switcher flips between them in one click. Name them (up to 16 characters each) and turn on Show workspace names in the widget to get tabs instead of dots. The wallpaper stays shared until you pick Only for workspace N in the background window.',
			},
			{
				q: 'How many workspaces do I get for free?',
				a:
					'New Tab Studio gives you three workspaces for free, and the switcher widget doesn’t count toward the free widget limit. Any other number, two included, takes PRO, and five is the maximum. If PRO lapses, the switcher goes back to showing three.',
			},
			{
				q:
					'Does copying widgets to another workspace count against the free limit?',
				a:
					'Yes, copied widgets count as ones you added yourself, so on the free plan a copy that would take you past three doesn’t go through. That applies to theme widgets too, which are free only until you copy them. Copying takes widgets alone, never the wallpaper or the workspace name.',
			},
		],
		related: ['wallpaper', 'todo-list', 'calendar', 'countdown'],
		keywords: [
			'new tab dashboard',
			'chrome dashboard',
			'multiple new tab pages',
			'new tab workspaces',
		],
	},
];
